import type { AxiosInstance } from 'axios';

import type {
  NewReseller,
  ResellerDetail,
  ResellerListRow,
  ResellerSession,
  ResellerTransaction,
} from '@domain/reseller/Reseller';
import type { ListPage, ListParams } from '@domain/customer/CustomerRepository';

import { ADMIN_API_BASE } from './client';

/**
 * The reseller endpoints predate the admin DataTable contract and return
 * `{results, count}` instead of `{rows, total, nextCursor}`. We translate
 * here so the page can use the standard DataTable component.
 */
function adapt<T>(payload: { results: T[]; count: number }): ListPage<T> {
  return {
    rows: payload.results ?? [],
    total: payload.count ?? 0,
  };
}

export class HttpResellerRepository {
  constructor(private readonly api: AxiosInstance) {}

  async list(
    params: ListParams & { status?: string; tier?: string; search?: string },
  ): Promise<ListPage<ResellerListRow>> {
    const query: Record<string, string | number> = { limit: params.limit };
    if (params.offset !== undefined) query.offset = params.offset;
    for (const k of ['status', 'tier', 'search'] as const) {
      if (params[k]) query[k] = params[k] as string;
    }
    const r = await this.api.get(`${ADMIN_API_BASE}/resellers/`, { params: query });
    return adapt<ResellerListRow>(r.data);
  }

  async get(id: string): Promise<ResellerDetail> {
    const r = await this.api.get(`${ADMIN_API_BASE}/resellers/${id}/`);
    return r.data as ResellerDetail;
  }

  async create(input: NewReseller): Promise<ResellerDetail & { generated_password: string }> {
    const r = await this.api.post(`${ADMIN_API_BASE}/resellers/`, input);
    return r.data as ResellerDetail & { generated_password: string };
  }

  async activate(id: string): Promise<ResellerDetail> {
    const r = await this.api.post(`${ADMIN_API_BASE}/resellers/${id}/activate/`);
    return r.data as ResellerDetail;
  }

  async suspend(id: string, reason: string): Promise<ResellerDetail> {
    const r = await this.api.post(
      `${ADMIN_API_BASE}/resellers/${id}/suspend/`,
      { reason },
    );
    return r.data as ResellerDetail;
  }

  async reactivate(id: string): Promise<ResellerDetail> {
    const r = await this.api.post(`${ADMIN_API_BASE}/resellers/${id}/reactivate/`);
    return r.data as ResellerDetail;
  }

  async terminate(id: string, reason: string): Promise<ResellerDetail> {
    const r = await this.api.post(
      `${ADMIN_API_BASE}/resellers/${id}/terminate/`,
      { reason },
    );
    return r.data as ResellerDetail;
  }

  async credit(
    id: string,
    amount: string,
    description: string,
  ): Promise<ResellerDetail> {
    const r = await this.api.post(
      `${ADMIN_API_BASE}/resellers/${id}/credit/`,
      { amount, description },
    );
    return r.data as ResellerDetail;
  }

  async rotatePassword(id: string): Promise<{ new_password: string; sessions_disconnected: number }> {
    const r = await this.api.post(
      `${ADMIN_API_BASE}/resellers/${id}/rotate-password/`,
      {},
    );
    return r.data as { new_password: string; sessions_disconnected: number };
  }

  async listSessions(
    resellerId: string,
    params: ListParams,
  ): Promise<ListPage<ResellerSession>> {
    const r = await this.api.get(
      `${ADMIN_API_BASE}/resellers/${resellerId}/sessions/`,
      { params: { limit: params.limit, offset: params.offset ?? 0 } },
    );
    return adapt<ResellerSession>(r.data);
  }

  async listTransactions(
    resellerId: string,
    params: ListParams,
  ): Promise<ListPage<ResellerTransaction>> {
    const r = await this.api.get(
      `${ADMIN_API_BASE}/resellers/${resellerId}/billing/`,
      { params: { limit: params.limit, offset: params.offset ?? 0 } },
    );
    return adapt<ResellerTransaction>(r.data);
  }
}
